import { createContext, forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useGlobal } from "../../core/Global"
import { HereGeocodePosition } from "../../models/MapModels"
import { DARK_TILES_CLASS, isRealPosition, TILE_URL, US_CENTER } from "./mapUtils"
import "./BaseMap.css"

/**
 * The map every screen builds on.
 *
 * It owns only what all of them share: creating the Leaflet map and removing it again, the
 * tiles in both themes, following the size of its box, and zoom / centre. Everything a screen
 * shows -- a job, technicians, metros -- is a layer component placed inside it, which reaches
 * the map through useBaseMap() and takes its own markers away when it unmounts.
 *
 *   <BaseMap height={400} center={job.position} zoom={12}>
 *       <JobMarkerLayer position={job.position} />
 *       <MetroLayers metros={metros} />
 *   </BaseMap>
 *
 * Children render once the map exists, next to it in a positioned box, so a child that renders
 * markup (a button, a legend) sits over the map with absolute positioning.
 */

export interface BaseMapContextValue {
    L: any
    map: any
    zoom: number
}

const BaseMapContext = createContext<BaseMapContextValue | undefined>(undefined)

/** The map and Leaflet itself, for a layer inside a BaseMap. */
export const useBaseMap = (): BaseMapContextValue => {
    const ctx = useContext(BaseMapContext)
    if (!ctx) throw new Error("useBaseMap() is only for components inside a <BaseMap>.")
    return ctx
}

export interface FitOptions {
    /** Space kept around the points, in pixels. */
    padding?: number
    /** How far a fit may zoom in -- a single point, or two close together, would go to street level. */
    maxZoom?: number
    animate?: boolean
}

export interface BaseMapRef {
    /** The Leaflet map, for what the props do not cover. */
    getMap: () => any
    /** Brings all the given positions into view; one position is centred at maxZoom. */
    fitTo: (positions: (HereGeocodePosition | undefined | null)[], options?: FitOptions) => void
    flyTo: (position: HereGeocodePosition, zoom?: number) => void
}

interface IProps {
    height?: number | string
    className?: string
    /** Where the map opens. A later change pans the map there. */
    center?: HereGeocodePosition
    /** The zoom the map opens at. */
    zoom?: number
    minZoom?: number
    maxZoom?: number
    /** The + / - buttons, bottom right. On by default. */
    zoomControl?: boolean
    scrollWheelZoom?: boolean
    onReady?: (map: any) => void
    onZoomChanged?: (zoom: number) => void
    onCenterChanged?: (center: HereGeocodePosition) => void
    onClick?: (position: HereGeocodePosition) => void
    children?: ReactNode
}

const DEFAULT_ZOOM = 6

const BaseMap = forwardRef<BaseMapRef, IProps>((props, ref) => {

    const L = window.GetLeafletInstance()
    const [global] = useGlobal()
    const dark = !!global.darkMode

    const containerRef = useRef<HTMLDivElement>(null)
    const tilesRef = useRef<any>()
    const [map, mapSet] = useState<any>()
    const [zoom, zoomSet] = useState<number>(props.zoom ?? DEFAULT_ZOOM)

    // The handlers are wired once, when the map is made; through this they always call the
    // props of the latest render, not the ones of the first.
    const propsRef = useRef(props)
    propsRef.current = props

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const start = isRealPosition(props.center) ? props.center : US_CENTER
        const tiles = L.tileLayer(TILE_URL, { opacity: .7, className: dark ? DARK_TILES_CLASS : "" })
        const m = L.map(el, {
            zoomControl: false,
            attributionControl: false,
            center: [start.lat, start.lng],
            zoom: props.zoom ?? DEFAULT_ZOOM,
            minZoom: props.minZoom ?? 3,
            maxZoom: props.maxZoom ?? 18,
            scrollWheelZoom: props.scrollWheelZoom ?? true,
            layers: [tiles]
        })
        tilesRef.current = tiles

        if (props.zoomControl !== false) L.control.zoom({ position: "bottomright" }).addTo(m)

        m.on("zoomend", () => {
            const z = m.getZoom()
            zoomSet(z)
            propsRef.current.onZoomChanged?.(z)
        })
        m.on("moveend", () => {
            const c = m.getCenter()
            propsRef.current.onCenterChanged?.({ lat: c.lat, lng: c.lng })
        })
        m.on("click", (e: any) => propsRef.current.onClick?.({ lat: e.latlng.lat, lng: e.latlng.lng }))

        // The box changes size without the window doing so -- a drawer widening, a side panel
        // sliding in -- and Leaflet only measures on a window resize, leaving grey tiles.
        const observer = new ResizeObserver(() => m.invalidateSize())
        observer.observe(el)

        mapSet(m)
        propsRef.current.onReady?.(m)

        return () => {
            observer.disconnect()
            m.remove()
            tilesRef.current = undefined
            mapSet(undefined)
        }
        // Made once; the props it reads are where the map starts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // The theme can change while the map is open.
    useEffect(() => {
        const el = tilesRef.current?.getContainer?.()
        if (el) el.classList.toggle(DARK_TILES_CLASS, dark)
    }, [dark, map])

    // A new centre pans there; the first one is where the map was made.
    const centerLat = props.center?.lat, centerLng = props.center?.lng
    const firstCenter = useRef(true)
    useEffect(() => {
        if (!map) return
        if (firstCenter.current) { firstCenter.current = false; return }
        if (isRealPosition(props.center)) map.panTo([props.center.lat, props.center.lng])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, centerLat, centerLng])

    useImperativeHandle(ref, () => ({
        getMap: () => map,
        fitTo: (positions, options) => {
            if (!map) return
            const real = positions.filter(isRealPosition)
            if (real.length === 0) return
            const maxZoom = options?.maxZoom ?? 14
            const animate = options?.animate ?? true
            if (real.length === 1) {
                map.setView([real[0].lat, real[0].lng], Math.max(map.getZoom(), Math.min(maxZoom, 12)), { animate })
                return
            }
            const pad = options?.padding ?? 50
            map.fitBounds(L.latLngBounds(real.map(p => [p.lat, p.lng])), { padding: [pad, pad], maxZoom, animate })
        },
        flyTo: (position, z) => {
            if (map && isRealPosition(position)) map.flyTo([position.lat, position.lng], z ?? map.getZoom())
        }
    }), [map, L])

    return (
        <div className={"base-map" + (props.className ? " " + props.className : "")}
            style={{ height: props.height ?? "100%" }}>
            <div ref={containerRef} className="base-map-canvas" />
            {
                map &&
                <BaseMapContext.Provider value={{ L, map, zoom }}>
                    {props.children}
                </BaseMapContext.Provider>
            }
        </div>
    )
})

export default BaseMap
